import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  Link,
  TextField,
  Typography,
} from "@mui/material";
import { axiosInstance } from "../../../utils/axiosInstance";
import Message from "../../Alert/Message";

interface CmmsSettingsResponse {
  configured: boolean;
  keyHint: string | null;
  envFallbackConfigured?: boolean;
}

export default function CmmsSettings() {
  const [message, setMessage] = useState<string | boolean>(false);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [apiKey, setApiKey] = useState("");
  const [status, setStatus] = useState<CmmsSettingsResponse | null>(null);

  const load = async () => {
    setFetching(true);
    try {
      const res = await axiosInstance.get<CmmsSettingsResponse>("/settings/cmms");
      setStatus(res.data);
    } catch (e) {
      console.error(e);
      setMessage("Could not load CMMS settings.");
    }
    setFetching(false);
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    setLoading(true);
    setMessage(false);
    try {
      await axiosInstance.put("/settings/cmms", { apiKey });
      setApiKey("");
      setMessage("API key saved.");
      await load();
    } catch (e: any) {
      console.error(e);
      setMessage(
        e?.response?.data?.message || "Failed to save API key."
      );
    }
    setLoading(false);
  };

  const clearKey = async () => {
    setLoading(true);
    setMessage(false);
    try {
      await axiosInstance.put("/settings/cmms", { apiKey: "" });
      setApiKey("");
      setMessage("Stored API key cleared.");
      await load();
    } catch (e: any) {
      console.error(e);
      setMessage(
        e?.response?.data?.message || "Failed to clear API key."
      );
    }
    setLoading(false);
  };

  return (
    <Box className="sq-settings-panel">
      <Message message={message} setMessage={setMessage} />
      <Card variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}>
        <Typography variant="h6" component="h2" gutterBottom>
          Nitro CMMS
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Open work orders are loaded from{" "}
          <Link
            href="https://docs.nitrocmms.co.uk/"
            target="_blank"
            rel="noopener noreferrer"
          >
            Nitro CMMS
          </Link>{" "}
          using an API key. The key is stored on the server and never shown in full after saving.
          Map each store to a Nitro location ID under Locations (edit a location).
        </Typography>
        {fetching ? (
          <Typography variant="body2">Loading…</Typography>
        ) : (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2">
              Key in database:{" "}
              {status?.configured
                ? `configured (${status.keyHint})`
                : "not set"}
            </Typography>
            {status?.envFallbackConfigured ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Server environment variable <code>NITRO_CMMS_API_KEY</code> is
                set and can be used when no database key is stored.
              </Typography>
            ) : null}
          </Box>
        )}
        <TextField
          label="API key"
          type="password"
          value={apiKey}
          onChange={(e) => setApiKey(e.target.value)}
          fullWidth
          autoComplete="off"
          placeholder="Paste new key to replace stored value"
          sx={{ mb: 2 }}
        />
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Button
            variant="contained"
            onClick={save}
            disabled={loading || fetching}
          >
            Save API key
          </Button>
          <Button
            variant="outlined"
            color="warning"
            onClick={clearKey}
            disabled={loading || fetching || !status?.configured}
          >
            Clear stored key
          </Button>
        </Box>
      </Card>
    </Box>
  );
}
